import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * エンジニアブログ — スクロール駆動アニメーション
 *
 * デスクトップ:
 *   - __outer の高さを動的に設定 (viewport + カードのスクロール量)
 *   - __inner が position: sticky でビューポートに固定
 *   - __cards を translateY でスクロール量に連動して上に移動
 *
 * モバイル:
 *   - blog__nav を sticky で上部に固定（SCSS）
 *   - blog__cards は通常スクロール（JS制御なし）
 */

const BLOCK = "blog";
const BLOG_BP_DESKTOP = 768;
const BLOG_MOBILE_ACCENT_TRIGGER_OFFSET = 56;
const BLOG_DESKTOP_ACCENT_TRIGGER_OFFSET = 100;
const BLOG_DESKTOP_SCROLL_TRIGGER_START = "top top";
const BLOG_DESKTOP_SCROLL_TRIGGER_END = "bottom bottom";
const BLOG_MOBILE_SCROLL_TRIGGER_START = "top bottom";
const BLOG_MOBILE_SCROLL_TRIGGER_END = "bottom top";
const BLOG_VISIBLE_PROGRESS_FACTOR = 0.6;

/**
 * エンジニアブログセクションのスクロールアニメーション初期化
 *
 * - デスクトップ: カードがスクロール量に応じて上にスライド
 * - モバイル: 通常スクロール
 *
 */
export function initScrollBlog() {
  // --- 1. 必要な要素を取得 ---
  const outer = document.getElementById("blog-outer"); // sticky領域の外枠
  const cardsWrap = document.getElementById("blog-cards"); // カード群ラッパー
  if (!outer || !cardsWrap) return;

  const section = document.getElementById("blog");
  const fill = section
    ? document.getElementById("blog-progress-fill")
    : null; // 進捗バー
  const numLabel = section
    ? document.getElementById("blog-progress-num")
    : null; // 進捗数値
  const cards = Array.from(cardsWrap.querySelectorAll(".js-blogCard")); // 各カード

  let scrollAmount = 0; // スクロール可能な量
  let isMobile = window.innerWidth < BLOG_BP_DESKTOP;
  let trigger = null;

  // --- 2. レイアウト初期化関数 ---
  /**
   * 画面幅に応じてレイアウト・スクロール量を再計算
   * - デスクトップ: sticky領域の高さを調整
   * - モバイル: 通常スクロール
   */
  function setup() {
    isMobile = window.innerWidth < BLOG_BP_DESKTOP;

    if (!isMobile) {
      // デスクトップ: sticky領域の高さを調整
      cardsWrap.style.opacity = "1";
      cardsWrap.style.transform = "";
      const cardsHeight = cardsWrap.scrollHeight;
      const vh = window.innerHeight;
      scrollAmount = Math.max(0, cardsHeight - vh + 80); // 80pxは余白調整
      outer.style.height = vh + scrollAmount + "px";
    } else {
      // モバイル: 通常スクロール
      cardsWrap.style.transform = "";
      cardsWrap.style.opacity = "1";
      scrollAmount = 0;
    }
  }

  // --- 3. スクロール量に応じてカードを動かす関数 ---
  /**
   * スクロール量に応じてカード群をtranslateYで上にスライド
   * - 進捗バー・進捗数値も更新
   * - 各カードのis-visibleクラスも付与
   */

  function update(progress = 0) {
    const clampedProgress = Math.max(0, Math.min(1, progress));

    if (isMobile) {
      cardsWrap.style.opacity = "1";
      cardsWrap.style.transform = "";
    }

    // デスクトップ: カードを上にスライド
    if (!isMobile && scrollAmount <= 0) {
      cardsWrap.style.transform = "";
    }

    // すべてのカードからis-accentを一旦外す
    cards.forEach((c) => c.classList.remove("is-accent"));

    // --- スクロール進捗計算 ---
    let clamped, pct;
    if (!isMobile) {
      clamped = scrollAmount * clampedProgress; // デスクトップ: ScrollTrigger進捗を使用
      pct = scrollAmount > 0 ? clamped / scrollAmount : 0; // 進捗率
      cardsWrap.style.transform = `translateY(${-clamped}px)`;
    } else {
      // モバイル: section基準のScrollTrigger進捗
      const mobileRange = section ? section.offsetHeight + window.innerHeight : 0;
      pct = clampedProgress;
      clamped = mobileRange * pct;
    }

    // 進捗バーの高さを更新
    if (fill) {
      fill.style.height = pct * 100 + "%";
    }

    // 進捗数値（例: 02 / 05）を更新
    if (numLabel && cards.length > 0) {
      const idx = Math.min(
        cards.length - 1,
        Math.floor(pct * cards.length)
      );
      const current = String(idx + 1).padStart(2, "0");
      const total = String(cards.length).padStart(2, "0");
      numLabel.textContent = `${current} / ${total}`;
    }

    // 各カードのis-visibleクラス付与（アニメーション用）
    cards.forEach((c, i) => {
      if (clamped >= (scrollAmount / cards.length) * i * BLOG_VISIBLE_PROGRESS_FACTOR) {
        c.classList.add("is-visible");
      }
    });

    // PCではis-accentを付与しない
    if (!isMobile) return;

    // --- headerラインより下に見えている先頭カードにis-accentを付与 ---
    const headerElem = section
      ? section.querySelector('#blog-header, .blog__header')
      : null;
    const navElem = section ? section.querySelector('.blog__nav') : null;
    if (cards.length > 0) {
      let triggerLine = null;

      // モバイルはstickyナビの下端を基準にし、視覚上の切替位置を少し下げる
      if (isMobile && navElem) {
        triggerLine =
          navElem.getBoundingClientRect().bottom + BLOG_MOBILE_ACCENT_TRIGGER_OFFSET;
      } else if (headerElem) {
        triggerLine =
          headerElem.getBoundingClientRect().bottom + BLOG_DESKTOP_ACCENT_TRIGGER_OFFSET;
      } else if (navElem) {
        triggerLine = navElem.getBoundingClientRect().bottom;
      }

      if (triggerLine === null) return;

      // すべてのカードからis-accentを外す
      cards.forEach((c) => c.classList.remove("is-accent"));

      // bottomが基準ラインより下にある最初のカードをaccent対象にする
      // 先頭カードが隠れたら次カードへ自然に切り替わる
      const accentIdx = cards.findIndex((card) => {
        const cardRect = card.getBoundingClientRect();
        return cardRect.bottom > triggerLine;
      });

      if (accentIdx >= 0) {
        cards[accentIdx].classList.add("is-accent");
      }
    }
  }

  // --- 4. 初期化・イベント登録 ---
  const createTrigger = () => {
    setup();

    if (trigger) {
      trigger.kill();
      trigger = null;
    }

    if (!isMobile) {
      if (cards.length > 0) {
        cards[0].classList.add("is-visible");
      }

      // デスクトップはouterのstickyスクロール区間を監視
      trigger = ScrollTrigger.create({
        trigger: outer,
          start: BLOG_DESKTOP_SCROLL_TRIGGER_START,
          end: BLOG_DESKTOP_SCROLL_TRIGGER_END,
        onUpdate: (self) => update(self.progress),
      });
      update(trigger.progress);
      return;
    }

    // モバイルはsection全体の通過進捗を監視
    trigger = ScrollTrigger.create({
      trigger: section,
      start: BLOG_MOBILE_SCROLL_TRIGGER_START,
      end: BLOG_MOBILE_SCROLL_TRIGGER_END,
      onUpdate: (self) => update(self.progress),
    });
    update(trigger.progress);
  };

  createTrigger();

  window.addEventListener("resize", () => {
    createTrigger();
  });
}

